/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useMemo, useState} from "react"
import {Col, Row, Spin} from "antd"
import {SAPCheckPoints} from "./parts"
import {academicCourseGradesService, instructionalService, userServiceV3} from "services"
import {ProfileScore} from "../Common"
import {
    getFieldLabel,
    getFullName,
    getStudentTotalProgress,
    getTotalHourStatistics,
    getUserPhotoUrl,
    handleError,
    isFieldActive
} from "helpers"
import styles from "./Academic.module.css"
import {useStores} from "stores/useStores"
import {useDepartmentStudent, useModel, useVisible} from "hooks"
import {EditSapStatusPopup} from "../Common/ProfileScore/EditSapStatusPopup"
import Courses from "./parts/Courses/Courses"
import {HomeCalendar} from "sections/Homepage/parts"
import {AcademicPlans} from "types/academicPlans"
import {ShiftWorkWeek} from "types/attendance"
import {ProgressItem, StudentInfo, StudentScore, TotalProgress} from "uiKit"
import {Auth} from "types/auth"
import {get, head} from "lodash"
import {Settings} from "types/settings"
import cx from "classnames"
import {getDefaultOrFirstProfile} from "helpers/task"
import moment from "moment"
import {useTranslation} from "react-i18next"

type Props = {
    studentId: number
    departmentId: number
    userId: number
}

export function Academic(props: Props) {
    const {studentId, userId} = props
    const {t} = useTranslation()
    const [courses, setCourses] = useState([])
    const [academicData, setAcademicData] = useState<{
        lda?: string
        sapStatus?: AcademicPlans.SapStatus
        sapDate?: string
        totalHoursCompleted?: number
        cgpa?: number
        shiftWorkWeeks?: ShiftWorkWeek[]
        startDate?: string
        contractEndDate?: string
        projectedEndDate?: string
        averageAttendancePercentage?: number
        missedAttendanceHours?: number
    }>()
    const model = useModel()
    const showTotalHours = isFieldActive(model, Settings.AcademicLabel.TotalHours)
    const totalHoursLabel = getFieldLabel(model, Settings.AcademicLabel.TotalHours)
    const [user, setUser] = useState<any>()

    const popupForEditingSapStatus = useVisible()
    const {
        academics: {instructional}
    } = useStores()

    const studentProfileIds = useMemo(() => {
        const profileId = model.profileId
        let studentProfileIds = []
        if (!model.isStaffOrAdmin() && profileId) {
            studentProfileIds = [profileId]
        } else if (studentId) {
            studentProfileIds = [studentId]
        }

        return studentProfileIds
    }, [studentId])

    const convertUserInfo = (user: any, index, role, t) => {
        const userDateTimeFormat = model.getUserDateTimeFormat()
        const userDateFormat = model.getUserDateFormat()

        user.isActiveTableCol = index === 0
        user.fullName = getFullName(user)
        user.photoTab = user.photo ? getUserPhotoUrl(user.photo, 512) : "/image/DefaultAvatar.png"
        if (user.profiles.length > 0) {
            const profile = getDefaultOrFirstProfile(user, role)
            if (profile) {
                const ldaProfile = user.profiles.find((profile) => profile.lastDayOfAttendance)
                user.startDate = profile.startDate ? profile.startDate : ""
                user.degree = profile.degreeLevel ? profile.degreeLevel.name : ""
                user.programHtml = profile.currentMajor ? (profile.currentMajor as Auth.CurrentMajor).name : ""
                user.campusHtml = profile.campuses.map((campus) => campus.name).join(" ")
                user.roles = profile.roles.map((rol) => rol.name).join(" ")
                user.title = profile.title
                user.type = profile.type
                user.profileId = profile.id
                user.enrollmentType = profile.enrollmentType
                user.enrollmentId = profile.enrollmentId
                user.customProfileId = profile.customProfileId
                user.currentTerm = profile.currentTerm?.name
                user.programVersion = get(profile.currentMajorVersionId, "versionName")
                user.programVersionTotalClockHours = get(profile.currentMajorVersionId, "totalClockHours", 0)
                user.departmentSubunits = profile.departmentSubunits.map((subunit) => subunit.name).join(", ")
                user.lastDateAttended = ldaProfile?.lastDayOfAttendance
                    ? moment.utc(ldaProfile.lastDayOfAttendance).format(userDateFormat)
                    : "N/A"
                user.profileLastUpdated = profile.updatedAt
                    ? moment.utc(profile.updatedAt).format(userDateFormat)
                    : "N/A"
                user.academicStatsLastUpdated = profile.academicStatsLastUpdated
                    ? moment.utc(profile.academicStatsLastUpdated).format(userDateFormat)
                    : "N/A"
                user.relationshipsHtml = profile.relationships
                    .map(
                        (relationship) =>
                            `${relationship.relationship} - ${relationship.firstName} ${relationship.lastName}`
                    )
                    .join(", ")
            }
        }
        user.status = user.active === 1 ? t("common:message.active") : t("common:message.inactive")
        user.department = user.department.join(" ")
        user.emailHtml = (user.emails || []).map(({email}) => email).join(", ")
        user.phoneHtml = (user.phones || []).map(({phone}) => phone).join(", ")
        user.createdAt = moment(user.createdAt).format(userDateTimeFormat)

        return user
    }

    useEffect(() => {
        instructional.getAttendanceRatingByCourse(undefined, studentProfileIds, [])
        instructional.getMissedAttendanceStats({
            courseIds: undefined,
            termIds: [],
            studentProfileIds: studentProfileIds
        })
        getCourseGradeStudentStats()
        getCourses()
    }, [instructional, studentId])

    const getUserProfiles = async () => {
        if (studentProfileIds?.length) {
            const {data: users} = await userServiceV3.getAll({
                range: {
                    limit: 1,
                    offset: 0
                },
                filter: {
                    profileIds: studentProfileIds[0]
                },
                linkedObjects: true
            })
            const newUsers = users.map((user: any, index) => {
                return convertUserInfo(user, index, "student", t)
            })

            setUser(head(newUsers))
        }
    }

    useEffect(() => {
        getUserProfiles()
    }, [studentProfileIds])

    const getCourseGradeStudentStats = async () => {
        const profileId = model.profileId
        const params: {studentProfileId: number} = {
            studentProfileId: profileId
        }
        if (!model.isStaffOrAdmin() && profileId) {
            params.studentProfileId = profileId
        } else if (studentId) {
            params.studentProfileId = studentId
        }
        const {
            cgpa,
            lda,
            sapStatus,
            totalHoursCompleted = 0,
            sapDate,
            shiftWorkWeeks,
            projectedEndDate,
            contractEndDate,
            startDate,
            averageAttendancePercentage,
            missedAttendanceHours
        } = await academicCourseGradesService.getCourseGradeStudentStats(params)
        setAcademicData({
            lda,
            contractEndDate,
            startDate,
            projectedEndDate,
            shiftWorkWeeks,
            sapStatus,
            totalHoursCompleted,
            sapDate,
            cgpa,
            averageAttendancePercentage,
            missedAttendanceHours
        })
    }

    const getCourses = useCallback(async () => {
        try {
            const params: any = {
                range: {page: 1, pageSize: 1000},
                filter: {studentProfileIds, termIds: []}
            }
            const {data} = await instructionalService.getTermAndCourseList(params)
            setCourses(data)
        } catch (error) {
            handleError(error)
        }
    }, [studentProfileIds])

    const renderLeftInfo = (studentStats: Auth.StudentStatistics, activeProfileId: number) => {
        const activeProfile = user?.profiles?.find((profile) => profile.id === activeProfileId)
        const programVersionTotalClockHours = get(activeProfile, "currentMajorVersionId.totalClockHours", 0)
        const totalHours = getTotalHourStatistics(model, studentStats, programVersionTotalClockHours)

        return (
            <div className={styles.studentLeftInfo}>
                <TotalProgress percent={getStudentTotalProgress(studentStats)} />
                <ProgressItem
                    title="Total hours"
                    totalTitle={totalHoursLabel}
                    totalHours={totalHours}
                    showTotalHours={showTotalHours}
                    totalContractedHours={studentStats.totalHours}
                />
            </div>
        )
    }

    const renderRightInfo = (studentStats: Auth.StudentStatistics) => {
        return (
            <div className={styles.studentRightInfo}>
                <div className={cx(styles.studentLeftInfo, styles.studentRightInfo__margin)}>
                    <StudentScore studentStats={studentStats} />
                </div>
            </div>
        )
    }

    return (
        <div>
            {academicData && user ? (
                <>
                    <StudentInfo
                        student={user}
                        isDetailButtonVisible={false}
                        renderExtraPhoneEmail={({studentStats, activeProfileId}) =>
                            renderLeftInfo(studentStats, activeProfileId)
                        }
                        renderRightInfo={({studentStats}) => renderRightInfo(studentStats)}
                        isHideStudentDetailButton
                        defaultStudentProfileId={studentProfileIds[0]}
                    />
                    <div style={{marginTop: 20}}>
                        <ProfileScore
                            lastDayAttendance={academicData.lda}
                            sapDate={academicData.sapDate}
                            onlyShowCgpa
                            cgpa={academicData.cgpa}
                            onEditSapStatus={popupForEditingSapStatus.open}
                            isShowSapStatus
                            attendedHours={academicData.totalHoursCompleted}
                            isShowAttendedHours
                            isEditableSapStatus={model.isStaffOrAdmin()}
                            sapStatus={academicData.sapStatus}
                            missedHours={academicData?.missedAttendanceHours ?? 0}
                            attendanceRating={academicData?.averageAttendancePercentage ?? 0}
                        />
                    </div>

                    <Col span={24}>
                        <HomeCalendar
                            shiftData={{
                                shiftWorkWeeks: academicData.shiftWorkWeeks,
                                startDate: academicData.startDate,
                                contractEndDate: academicData.contractEndDate,
                                projectedEndDate: academicData.projectedEndDate
                            }}
                            showFullCalendar
                            studentProfileId={studentId}
                            showStatsInfo
                            userId={userId}
                            customCalendarHeight={612}
                        />
                    </Col>
                    <Row gutter={[10, 10]} style={{marginTop: 20}}>
                        <Col span={12}>
                            <div className={styles.courseInfo}>
                                <Courses courses={courses} />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className={styles.courseInfo}>
                                <SAPCheckPoints />
                            </div>
                        </Col>
                    </Row>
                    <EditSapStatusPopup
                        isShow={popupForEditingSapStatus.isVisible}
                        onClose={popupForEditingSapStatus.close}
                        studentProfileId={studentId}
                        sapStatus={academicData.sapStatus}
                        sapDate={academicData.sapDate}
                        onRefreshLda={getCourseGradeStudentStats}
                    />
                </>
            ) : (
                <div className={styles.spinWrapper}>
                    <Spin />
                </div>
            )}
        </div>
    )
}
